import { FEATURE_FLAG_NAMES } from "./feature-flag-name";
import type { FeatureFlags } from "./featureFlags";

const STORAGE_KEY = "volca-dev-flag-overrides";

export type FlagName =
  (typeof FEATURE_FLAG_NAMES)[keyof typeof FEATURE_FLAG_NAMES];

export type FlagOverrides = Partial<Record<FlagName, boolean>>;

const FLAG_NAMES = new Set<string>(Object.values(FEATURE_FLAG_NAMES));

export const isFlagName = (name: string): name is FlagName =>
  FLAG_NAMES.has(name);

export const readFlagOverrides = (scope: string): FlagOverrides => {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}:${scope}`);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const overrides: FlagOverrides = {};
    for (const [name, enabled] of Object.entries(parsed)) {
      if (isFlagName(name) && typeof enabled === "boolean") {
        overrides[name] = enabled;
      }
    }
    return overrides;
  } catch {
    return {};
  }
};

export const writeFlagOverrides = (scope: string, overrides: FlagOverrides) => {
  try {
    const key = `${STORAGE_KEY}:${scope}`;
    if (Object.keys(overrides).length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(overrides));
    }
  } catch {
    // Private mode can refuse storage. The override still applies in memory.
  }
};

export const applyFlagOverrides = (
  flags: FeatureFlags | undefined,
  overrides: FlagOverrides,
): FeatureFlags | undefined => {
  const names = Object.keys(overrides) as FlagName[];
  if (names.length === 0) return flags;
  const merged = { ...(flags ?? {}) } as FeatureFlags;
  for (const name of names) {
    const enabled = overrides[name];
    if (enabled === undefined) continue;
    merged[name] = { enabled };
  }
  return merged;
};
